import React, { useEffect, useState } from 'react';
import './TodayLine.css';

interface TodayLineProps {
  days: { day: number; month: string }[];
  gridRef: React.RefObject<HTMLDivElement>;
}

const monthNames = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
];

const TodayLine: React.FC<TodayLineProps> = ({ days, gridRef }) => {
  const [linePosition, setLinePosition] = useState<{ left: number; width: number } | null>(null);

  useEffect(() => {
    const calculatePosition = () => {
      if (!days.length || !gridRef.current) return;

      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const currentDay = today.getDate();

      // Find the current month in the days array
      const currentMonthName = monthNames[currentMonth];
      const currentYearShort = (currentYear % 100).toString().padStart(2, '0');
      const currentMonthLabel = `${currentMonthName} ${currentYearShort}`;

      const currentMonthIndex = days.findIndex(day => day.month === currentMonthLabel);
      
      if (currentMonthIndex === -1) {
        setLinePosition(null);
        return;
      }

      // Find all day-header elements
      const dayHeaders = gridRef.current.querySelectorAll('.day-header');
      
      if (dayHeaders.length === 0) {
        setLinePosition(null);
        return;
      }

      // Get the current month's header element
      const currentMonthHeader = dayHeaders[currentMonthIndex] as HTMLElement;
      
      if (!currentMonthHeader) {
        setLinePosition(null);
        return;
      }

      // Get the position and width of the current month column
      const headerRect = currentMonthHeader.getBoundingClientRect();
      const gridRect = gridRef.current.getBoundingClientRect();
      
      // Calculate relative position within the grid
      const columnLeft = headerRect.left - gridRect.left;
      const columnWidth = headerRect.width;

      // Calculate position within the current month (based on current day)
      const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      const dayProgress = (currentDay - 1) / (daysInCurrentMonth - 1); // Spread across the full month
      
      // Position the line within the month column
      const lineLeft = columnLeft + (dayProgress * columnWidth);

      setLinePosition({
        left: lineLeft,
        width: columnWidth
      });
    };

    // Calculate immediately
    calculatePosition();

    // Also calculate after delays to ensure everything is rendered
    const timeout1 = setTimeout(calculatePosition, 100);
    const timeout2 = setTimeout(calculatePosition, 500);

    // Add resize observer to recalculate on grid changes
    const resizeObserver = new ResizeObserver(calculatePosition);
    if (gridRef.current) {
      resizeObserver.observe(gridRef.current);
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      resizeObserver.disconnect();
    };
  }, [days, gridRef]);

  const today = new Date();
  const hebrewMonth = today.toLocaleString('he-IL', { month: 'long' });
  const year = today.getFullYear();

  if (!linePosition) return null;

  return (
    <div className="today-line-wrapper">
      <div
        className="today-line"
        style={{ 
          left: `${linePosition.left}px`,
        }}
        data-date={`${hebrewMonth} ${year}`}
      />
    </div>
  );
};

export default TodayLine;
